import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { User } from 'oidc-client';
import { bindActionCreators, Dispatch } from 'redux';
import { AppState } from '../../store';
import { Row, Col, Card, Spinner, Tabs, Tab, Button, Form, InputGroup, Nav, NavDropdown, Navbar, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { RouteComponentProps } from 'react-router';
import * as ReportStore from '../../store/Report';
import DatasetRefreshHistoryTable from './DatasetRefreshHistoryTable';
import ReportView from './ReportView';
import ReportAliasesTable from './ReportAliasesTable';
import RecordAccessControlTable from '../Common/RecordAccessControl/RecordAccessControlTable';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faExpandAlt, faFileClipboard, faFilePdf, faFilePowerpoint, faPencil } from '@fortawesome/pro-regular-svg-icons';
import EditStaleHoursModal from './Modals/EditStaleHoursModal';
import DatasetRefreshSchedule from './DatasetRefreshSchedule';
import { PowerBiObjectType } from '../../types/Common/PowerBiObjectType';
import CopyableSystemId from '../../components/copyableSystemId';
import { userHasAnyRole } from '@konicaminolta/common-react';
import { ROLE_FULL_ADMINISTRATOR } from '../../appRoles';
import NoPermissions from '../Common/NoPermissions/NoPermissions';
import { ExportFileType } from '../../types/Common/Enums/ExportFileType';
import { faUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';

enum ReportDetailsTabs {
    Report = 'report',
    Aliases = 'aliases',
    Permissions = 'permission',
    RefreshSchedule = 'refreshSchedule',
    RefreshHistory = 'refreshHistory'
}

type ReportDetailsProps = {
    dispatch: Dispatch;
    reportState: ReportStore.ReportState;
    user: User;
    actions: typeof ReportStore.actionCreators;
}
& RouteComponentProps<{reportId: string}>;

interface ReportDetailsState {
    tabKey: ReportDetailsTabs | ReportDetailsTabs.Report;
    showUpdateHoursUntilStaleModal: boolean;
    isLoading: boolean;
}

class ReportDetails extends React.Component<ReportDetailsProps, ReportDetailsState> {
    private reportViewChildRef: React.RefObject<ReportView>;
    public constructor(props: ReportDetailsProps) {
        super(props);
        this.state = {
            tabKey: ReportDetailsTabs.Report,
            showUpdateHoursUntilStaleModal: false,
            isLoading: true,
        }; 
        this.reportViewChildRef = React.createRef();
    }

    public componentDidMount() {
        this.props.actions.unloadReportDetails();
        this.ensureDataFetched();
    }

    public componentDidUpdate() {
        this.ensureDataFetched();
    }

    private ensureDataFetched() {
        this.props.actions.requestReportDetails(this.props.match.params.reportId);

        if (this.state.isLoading && !this.props.reportState.isLoadingDetails && this.props.reportState.isLoadedDetails) {
            this.setState({isLoading: false});
        }
    }

    private refreshReportDetails() {
        this.setState({isLoading: true});
        this.componentDidMount();
    }

    private handleUpdateStaleHoursModal() {
        // Refresh Report Details
        this.refreshReportDetails();
        
        this.ensureDataFetched();
        this.handleCloseEditStaleHoursModal();
    }

    private handleOpenEditStaleHoursModal() {
        if(userHasAnyRole(this.props.user, [ROLE_FULL_ADMINISTRATOR])) {
            this.setState({ showUpdateHoursUntilStaleModal: true });
        }
    }
    private handleCloseEditStaleHoursModal() {
        this.setState({ showUpdateHoursUntilStaleModal: false });
    }

    // Handle the NavMenu click for full screen and pass on the request to the child component
    onFullScreenClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        this.reportViewChildRef?.current?.powerBiEmbedFullScreen();
    };

    showExportFileRequestModal = (exportFileType: ExportFileType) => {
        this.reportViewChildRef?.current?.exportFileRequest(exportFileType);
    }

    public render() {
        let report = this.props.reportState.data.find(x => x.id === this.props.match.params.reportId);
        if(this.props.reportState.hasPermissionsToDetails && !this.props.reportState.isLoadedDetails && report === undefined) {
            return (
                <div className="d-flex justify-content-center mt-3">
                    <Spinner animation="border" variant="primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                </div>
            );
        }



        if (!this.props.reportState.hasPermissionsToDetails) {
            return (
                <NoPermissions />
            );
        }

        if(report === undefined) {
            return (
                <div></div>
            );
        }

        report.type == PowerBiObjectType.Report;
        let aliasName = report?.aliasName ? report?.aliasName : report?.id;
        const reportShareUrl  = `${window.location.origin}/powerbireportviewer/reports/viewreport/${aliasName}`;
        
        return (
            <React.Fragment>
                <div id="detailsContainer">
                <Row>
                    <Col>
                        <Card className="panel">
                            <Card.Header>
                                <Row>
                                    <Col md={12}>
                                        <h3>Report Details</h3>
                                    </Col>
                                </Row>
                            </Card.Header>
                            <Card.Body>
                            <Row>
                                <Col lg={3} className="mr-1">
                                    <Card>
                                        <Card.Body>
                                            <Row>
                                                <Col
                                                    className="text-center"
                                                    xs={12}
                                                >
                                                    <h2>
                                                        {report?.name}
                                                    </h2>
                                                </Col>
                                            </Row>
                                            <Row className="mt-1">
                                                <small>
                                                    <Col className="d-flex flex-column align-items-center">
                                                        Report ID: {report?.id ? report?.id : ""}
                                                    </Col>
                                                </small>
                                            </Row>
                                                <Row className="mt-4">
                                                    <Col className="text-end">
                                                        <strong>
                                                            Group:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                        {
                                                            report?.groupName
                                                        }
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col className="text-end">
                                                        <strong>
                                                            Report Type:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                        {report?.reportType ? report.reportType : ''}
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col className="text-end md-6">
                                                        <strong>
                                                            Report Is Stale:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                        {report?.isStale === true ? "Yes" : "No" }
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col className="text-end md-6">
                                                        <strong>
                                                            Last Data Refresh Date:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                        {report?.lastDatasetRefreshHistoryDate ? format(new Date(report.lastDatasetRefreshHistoryDate), 'yyyy-MM-dd hh:mm:ss')  : ''}
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col className="text-end">
                                                        <strong>
                                                            Hours until stale:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                        <Row>
                                                            <Col>
                                                                {report?.hoursUntilStale ? report.hoursUntilStale : ''}
                                                            </Col>
                                                            <Col>
                                                                {
                                                                    userHasAnyRole(this.props.user, [ROLE_FULL_ADMINISTRATOR]) &&
                                                                    <div 
                                                                        onClick={() => this.handleOpenEditStaleHoursModal()}
                                                                    >
                                                                        <FontAwesomeIcon icon={faPencil}/>
                                                                    </div>
                                                                }
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col xs={6} className="text-end">
                                                        <strong>
                                                            Tenant:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                    <div className="text-break">
                                                        <CopyableSystemId
                                                        data={
                                                            report?.tenantDomain || ''
                                                        }
                                                        />
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col xs={6} className="text-end">
                                                        <strong>
                                                            Tenant Id:
                                                        </strong>
                                                    </Col>
                                                    <Col>
                                                        <div className="text-break">
                                                        <CopyableSystemId
                                                        data={
                                                            report?.tenantId || ''
                                                        }
                                                        />
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <br />
                                                <Row className="mt-4">
                                                    <strong>Report Share URL</strong>
                                                </Row>
                                                <Row>
                                                    <InputGroup className="mb-3">
                                                    <Form.Control
                                                            aria-label="Report Share URL"
                                                            aria-describedby='reportShareUrl'
                                                            value={reportShareUrl}
                                                            readOnly
                                                    />
                                                    <OverlayTrigger
                                                        placement='top'
                                                        overlay={<Tooltip><span>Copy report share URL to clipboard</span></Tooltip>}
                                                    >
                                                    <Button className="btn btn-info" onClick={() => navigator.clipboard.writeText(reportShareUrl)}><FontAwesomeIcon icon={faFileClipboard} /></Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger
                                                        placement='top'
                                                        overlay={<Tooltip><span>Open report share URL in new tab</span></Tooltip>}
                                                    >
                                                    <Button className="btn btn-info" onClick={() => window.open(reportShareUrl, '_blank', 'noopener,noreferrer')}><FontAwesomeIcon icon={faUpRightFromSquare} /></Button>
                                                    </OverlayTrigger>
                                                    </InputGroup>
                                                </Row>
                                                <Row className="mt-4">
                                                    <strong>Power BI URL</strong>
                                                </Row>
                                                <Row>
                                                    <InputGroup className="mb-3">
                                                    <Form.Control
                                                            aria-label="Report URL"
                                                            aria-describedby='powerBiUrl'
                                                            value={report?.powerBiUrl}
                                                            readOnly
                                                    />
                                                    <OverlayTrigger
                                                        placement='top'
                                                        overlay={<Tooltip><span>Copy Power BI report URL to clipboard</span></Tooltip>}
                                                    >
                                                    <Button className="btn btn-info" onClick={() => navigator.clipboard.writeText(report?.powerBiUrl ?? "")}><FontAwesomeIcon icon={faFileClipboard} /></Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger
                                                        placement='top'
                                                        overlay={<Tooltip><span>Open Power BI URL in a new tab</span></Tooltip>}
                                                    >
                                                    <Button className="btn btn-info" onClick={() => window.open(report?.powerBiUrl ?? "", '_blank', 'noopener,noreferrer')}><FontAwesomeIcon icon={faUpRightFromSquare} /></Button>
                                                    </OverlayTrigger>
                                                    </InputGroup>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col lg={9}>
                                        {report?.id === this.props.match.params.reportId && (
                                            <Tabs
                                                activeKey={this.state.tabKey}
                                                onSelect={(x: string | null) =>
                                                    this.setState({ tabKey: x as ReportDetailsTabs })
                                                }
                                                className="mb-4"
                                                defaultActiveKey={
                                                    ReportDetailsTabs.Report
                                                }
                                                id="tab-details"
                                            >
                                                <Tab
                                                    eventKey={
                                                        ReportDetailsTabs.Report
                                                    }
                                                    title="Report"
                                                >
                                                    <Navbar collapseOnSelect expand="lg" className="flex-lg-nowrap p-0 bg-white">
                                                    <Nav className="menu-user ml-auto me-4 w-100 justify-content-end">
                                                    { 
                                                    report?.isOnDedicatedCapacity ?
                                                        <Nav.Item id="export-to-file" role="menu">
                                                            <NavDropdown
                                                                id="export-to-file-dropdown"
                                                                className='dropdown-menu-right'
                                                                title={
                                                                    <span><FontAwesomeIcon className="me-2 text-dark" icon={faDownload} />
                                                                        Export
                                                                    </span>
                                                                }
                                                                >
                                                                <NavDropdown.Item onClick={() => this.showExportFileRequestModal(ExportFileType.Pdf)}>
                                                                    <span><FontAwesomeIcon className="me-2 text-dark" icon={faFilePdf} />
                                                                        PDF
                                                                    </span>
                                                                </NavDropdown.Item>
                                                                <NavDropdown.Item onClick={() => this.showExportFileRequestModal(ExportFileType.Pptx)}>
                                                                    <span><FontAwesomeIcon className="me-2 text-dark" icon={faFilePowerpoint} />
                                                                        PPTX
                                                                    </span>
                                                                </NavDropdown.Item>
                                                            </NavDropdown>
                                                        </Nav.Item>
                                                        : ''
                                                    }
                                                        <Nav.Item id="report-fullscreen" role="button" onClick={this.onFullScreenClick}>
                                                            <div className='nav-link'>
                                                            <span><FontAwesomeIcon className="me-2 text-dark" icon={faExpandAlt} />Full Screen</span>
                                                            </div>
                                                        </Nav.Item>
                                                    </Nav>
                                                    </Navbar>
                                                    <ReportView
                                                       ref={this.reportViewChildRef}
                                                       reportId={report.id}
                                                       {...this.props}
                                                    />
                                                </Tab>
                                                <Tab
                                                    eventKey={
                                                        ReportDetailsTabs.Permissions
                                                    }
                                                    title="Permissions"
                                                >
                                                    <RecordAccessControlTable
                                                     {...this.props}
                                                     record={report}
                                                     recordType={PowerBiObjectType.Report}
                                                     />
                                                </Tab>
                                                <Tab
                                                    eventKey={
                                                        ReportDetailsTabs.Aliases
                                                    }
                                                    title="Aliases"
                                                >
                                                    <ReportAliasesTable
                                                     {...this.props}
                                                     reportId={report.id}
                                                     refreshReport={() => this.refreshReportDetails()}
                                                     />
                                                </Tab>
                                                <Tab
                                                    eventKey={
                                                        ReportDetailsTabs.RefreshSchedule
                                                    }
                                                    title="Refresh Schedule"
                                                >
                                                <DatasetRefreshSchedule
                                                    datasetId={report.datasetId}
                                                    {...this.props}
                                                />
                                                </Tab>
                                                <Tab
                                                    eventKey={
                                                        ReportDetailsTabs.RefreshHistory
                                                    }
                                                    title="Refresh History"
                                                >
                                                <DatasetRefreshHistoryTable
                                                    datasetId={report.datasetId}
                                                    {...this.props}
                                                />
                                                </Tab>
                                            </Tabs>
                                        )}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                {
                <React.Fragment>
                    <EditStaleHoursModal
                        dispatch={this.props.dispatch}
                        powerBiObject={report}
                        updateHoursUntilStale={() => this.handleUpdateStaleHoursModal()}
                        hideModal={() => this.handleCloseEditStaleHoursModal()}
                        showModal={this.state.showUpdateHoursUntilStaleModal}
                    />
                    </React.Fragment>
                }
                </div>
            </React.Fragment>

        );
    }
}

function mapStateToProps(state: AppState) {
    return {
        reportState: state.report,
        user: state.oidc.user,
    };
}

export default connect(
    mapStateToProps,
    (dispatch) => {
        return {
            dispatch: dispatch,
            actions: bindActionCreators(Object.assign(
                {},
                ReportStore.actionCreators
            ), dispatch )
        }
    },
)(ReportDetails as any);

